/**
 * Editable state, wire conversion, client-side validation and the division
 * conflict check for a Conversation info action's fields. The rules mirror
 * the wizard backend (utils/info-fields.ts) and the toolbar admin API
 * (infoFields.ts), so a list that passes here is accepted upstream; the
 * server remains the authoritative check. The shared cases in
 * test-fixtures/conversation-info-validation-cases.json hold all three to the
 * same answers.
 */
import {
  INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH,
  INFO_FIELD_LABEL_MAX_LENGTH,
  INFO_FIELD_MATCHES_MAX,
  INFO_FIELDS_MAX,
  INFO_FIELDS_MAX_IMPORTANT,
  INFO_LINK_PLACEHOLDERS_MAX,
  INFO_LINK_URL_MAX_LENGTH,
  INFO_MATCH_EQUALS_MAX_LENGTH,
  INFO_MATCH_NUMBER_MAX_LENGTH,
  INFO_MATCH_TEXT_MAX_LENGTH,
  type InfoField,
  type InfoFieldChannels,
  type InfoFieldDisplay,
  type InfoFieldMatch,
  type InfoMatchOp,
} from './wizard-backend';
import { t } from './language-manager';

export const CONVERSATION_INFO_TYPE = 'CONVERSATION_INFO';

/**
 * Values the extension's notification shows: the first marked fields that
 * have a value for the conversation, in list order. Not a limit on marks.
 */
export const NOTIFICATION_ROWS = 3;

export interface EditableInfoMatch {
  rowKey: string;
  op: InfoMatchOp;
  /** The compared value, whatever the operator (named after the v1 exact match); unused for `any`. */
  equals: string;
  text: string;
}

/** One info field as edited in the card. All inputs are kept as typed. */
export interface EditableInfoField {
  /** Stable React key; never sent. */
  rowKey: string;
  /** The toolbar's id once stored; a new field has none. */
  id?: string;
  label: string;
  /** Not shown for a link field, whose attribute is its first placeholder. */
  attributeKey: string;
  display: InfoFieldDisplay;
  /** Kept while the admin switches to Show value and back; sent only for `match`. */
  matches: EditableInfoMatch[];
  /** Kept while the admin switches mode; sent only for `link`. */
  linkUrl: string;
  notify: boolean;
  important: boolean;
  channels: InfoFieldChannels;
  /** UI-only: added with Add field and not yet confirmed with Save field; never sent. */
  unsaved?: boolean;
}

export type InfoFieldInput = 'label' | 'attributeKey' | 'matches' | 'linkUrl';
export type InfoMatchInput = 'equals' | 'text';

/** Per-row problems (i18n keys by input) plus a list-level problem, if any. */
export interface InfoFieldsValidation {
  rows: Array<Partial<Record<InfoFieldInput, string>>>;
  matchRows: Array<Array<Partial<Record<InfoMatchInput, string>>>>;
  block: string | null;
  hasErrors: boolean;
}

/** Operators in the order the condition select lists them. */
export const INFO_MATCH_OPS: readonly InfoMatchOp[] = ['equals', 'not_equals', 'contains', 'starts_with', 'any', 'gt', 'gte', 'lt', 'lte'];
const NUMBER_OPS: ReadonlySet<InfoMatchOp> = new Set(['gt', 'gte', 'lt', 'lte']);
const NUMBER_RE = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;

export function isNumberOp(op: InfoMatchOp): boolean {
  return NUMBER_OPS.has(op);
}

let nextRowKey = 0;
function newRowKey(): string {
  nextRowKey += 1;
  return `info-field-${nextRowKey}`;
}

// Bidi embedding, override and isolate controls: admin text must not reorder the agent's panel.
const BIDI_CONTROLS_RE = /[\u202a-\u202e\u2066-\u2069]/g;
const CONTROL_CHARS_RE = /[\u0000-\u001F\u007F-\u009F]/g;
const URL_FORBIDDEN_RE = /[\u0000-\u001F\u007F-\u009F\u202a-\u202e\u2066-\u2069]/;

/** What the server stores for a label, condition value or text: bidi and control characters stripped, whitespace collapsed, trimmed. */
export function cleanInfoText(value: string): string {
  return value.replace(BIDI_CONTROLS_RE, '').replace(/\s+/g, ' ').replace(CONTROL_CHARS_RE, '').trim();
}

/** Comparison form of a condition value (the extension compares participant values the same way). */
export function infoMatchKey(value: string): string {
  return value.trim().normalize('NFC').toLowerCase();
}

/** Web-messaging `context.*` attributes are set by the visitor's browser and can be spoofed. */
export function isCustomerSetAttribute(attributeKey: string): boolean {
  return attributeKey.trim().toLowerCase().startsWith('context.');
}

/**
 * The link template rules, in the toolbar's order. Returns the trimmed
 * template and its placeholder names, or the failing code. Exactly one
 * placeholder and nothing else (`{crmUrl}`) takes the whole address from
 * that attribute; the extension opens it only when it is a full https address.
 */
export function checkLinkTemplate(raw: string): { url: string; placeholders: string[] } | { code: string } {
  const url = raw.trim();
  if (!url) return { code: 'link_url_required' };
  if (url.length > INFO_LINK_URL_MAX_LENGTH) return { code: 'link_url_too_long' };
  if (URL_FORBIDDEN_RE.test(url)) return { code: 'link_url_invalid' };
  const whole = /^\{([^{}]*)\}$/.exec(url);
  if (whole) {
    const name = whole[1].trim();
    if (!name || name.length > INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH) return { code: 'link_placeholder_invalid' };
    return { url, placeholders: [name] };
  }
  if (!/^https?:\/\//i.test(url)) return { code: 'link_url_scheme' };

  const placeholders: string[] = [];
  let outside = '';
  for (let i = 0; i < url.length; i++) {
    const ch = url[i];
    if (ch === '}') return { code: 'link_placeholder_invalid' };
    if (ch !== '{') {
      outside += ch;
      continue;
    }
    const close = url.indexOf('}', i + 1);
    const open = url.indexOf('{', i + 1);
    if (close < 0 || (open >= 0 && open < close)) return { code: 'link_placeholder_invalid' };
    const name = url.slice(i + 1, close).trim();
    if (!name || name.length > INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH) return { code: 'link_placeholder_invalid' };
    placeholders.push(name);
    i = close;
  }
  if (/\s/.test(outside)) return { code: 'link_url_invalid' };
  if (placeholders.length === 0) return { code: 'link_placeholder_required' };
  if (placeholders.length > INFO_LINK_PLACEHOLDERS_MAX) return { code: 'too_many_placeholders' };

  const afterScheme = url.slice(url.indexOf('//') + 2);
  const authorityEnd = afterScheme.search(/[/?#]/);
  const authority = authorityEnd < 0 ? afterScheme : afterScheme.slice(0, authorityEnd);
  if (authority.includes('{')) return { code: 'link_url_host_placeholder' };

  const sample = (fill: string) => url.replace(/\{[^}]*\}/g, fill);
  let parsed: URL;
  let other: URL;
  try {
    parsed = new URL(sample('x'));
    other = new URL(sample('y'));
  } catch {
    return { code: 'link_url_invalid' };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return { code: 'link_url_scheme' };
  if (!parsed.hostname || parsed.username || parsed.password) return { code: 'link_url_invalid' };
  // A value must never decide where the link goes (e.g. `https:///{id}` parses with the value as the host).
  if (other.host !== parsed.host) return { code: 'link_url_host_placeholder' };
  return { url, placeholders };
}

const LINK_URL_ERROR_KEYS: Record<string, string> = {
  link_url_required: 'settings-actions-info-error-link-url-required',
  link_url_too_long: 'settings-actions-info-error-link-url-too-long',
  link_url_invalid: 'settings-actions-info-error-link-url-invalid',
  link_url_scheme: 'settings-actions-info-error-link-url-scheme',
  link_url_host_placeholder: 'settings-actions-info-error-link-url-host',
  link_placeholder_invalid: 'settings-actions-info-error-link-placeholder-invalid',
  link_placeholder_required: 'settings-actions-info-error-link-placeholder-required',
  too_many_placeholders: 'settings-actions-info-error-link-placeholders-max',
};

/** The attribute a link field reads first: its first placeholder, once the template is valid. */
export function linkAttributeKey(field: Pick<EditableInfoField, 'linkUrl'>): string | null {
  const checked = checkLinkTemplate(field.linkUrl);
  return 'code' in checked ? null : checked.placeholders[0];
}

/** The attribute holding a link field's whole address (`{crmUrl}`), once valid; null for a typed site name. */
export function wholeAddressAttribute(field: Pick<EditableInfoField, 'linkUrl'>): string | null {
  const checked = checkLinkTemplate(field.linkUrl);
  return 'code' in checked || !checked.url.startsWith('{') ? null : checked.placeholders[0];
}

export function emptyInfoMatch(): EditableInfoMatch {
  return { rowKey: newRowKey(), op: 'equals', equals: '', text: '' };
}

export function emptyInfoField(): EditableInfoField {
  return {
    rowKey: newRowKey(),
    label: '',
    attributeKey: '',
    display: 'value',
    matches: [emptyInfoMatch()],
    linkUrl: '',
    notify: false,
    important: false,
    channels: 'all',
  };
}

function toEditableMatch(m: InfoFieldMatch): EditableInfoMatch {
  const op: InfoMatchOp = m.op ?? 'equals';
  return { rowKey: newRowKey(), op, equals: (op === 'equals' ? m.equals : m.value) ?? '', text: m.text };
}

export function toEditableInfoField(field: InfoField): EditableInfoField {
  const matches = field.display === 'match' && field.matches && field.matches.length > 0
    ? field.matches.map(toEditableMatch)
    : [emptyInfoMatch()];
  return {
    rowKey: newRowKey(),
    ...(field.id ? { id: field.id } : {}),
    label: field.label,
    attributeKey: field.attributeKey ?? '',
    display: field.display,
    matches,
    linkUrl: field.link?.url ?? '',
    notify: field.notify === true,
    important: field.important === true,
    channels: field.channels === 'voice' || field.channels === 'digital' ? field.channels : 'all',
  };
}

/**
 * Exact upstream shape. Every condition row is sent, so server error indices
 * match what validateInfoFields() checked. `important` and `link` are always
 * sent: omitted, the toolbar would keep the stored values (the contract for
 * pages that cannot edit them). A link carries no text: agents see a generic
 * translated one.
 */
export function toWireInfoField(field: EditableInfoField): InfoField {
  const isLink = field.display === 'link';
  const isExternalTag = field.display === 'externalTag';
  const wire: InfoField = {
    ...(field.id ? { id: field.id } : {}),
    label: cleanInfoText(field.label),
    ...(isExternalTag ? {} : { attributeKey: (isLink ? linkAttributeKey(field) : null) ?? field.attributeKey.trim() }),
    display: field.display,
    notify: field.notify,
    important: field.important,
    link: isLink ? { url: field.linkUrl.trim() } : null,
  };
  if (field.display === 'match') {
    wire.matches = field.matches.map((m): InfoFieldMatch => {
      const text = cleanInfoText(m.text);
      if (m.op === 'equals') return { equals: cleanInfoText(m.equals), text };
      if (m.op === 'any') return { op: 'any', text };
      return { op: m.op, value: cleanInfoText(m.equals), text };
    });
  }
  if (field.channels !== 'all') wire.channels = field.channels;
  return wire;
}

export function countNotifyFields(fields: ReadonlyArray<{ notify: boolean }>): number {
  return fields.filter((f) => f.notify).length;
}

export function countImportantFields(fields: ReadonlyArray<{ important: boolean }>): number {
  return fields.filter((f) => f.important).length;
}

/** The value problem of one condition (i18n key), ignoring duplicates. */
function conditionValueError(m: EditableInfoMatch): string | null {
  if (m.op === 'any') return null;
  const value = cleanInfoText(m.equals);
  if (isNumberOp(m.op)) {
    return value && value.length <= INFO_MATCH_NUMBER_MAX_LENGTH && NUMBER_RE.test(value)
      ? null
      : 'settings-actions-info-error-number';
  }
  return value && value.length <= INFO_MATCH_EQUALS_MAX_LENGTH ? null : 'settings-actions-info-error-equals';
}

/** What makes two conditions the same (the server's duplicate rule); null for a condition with a value problem. */
function conditionKey(m: EditableInfoMatch): string | null {
  if (m.op === 'any') return 'any';
  if (conditionValueError(m)) return null;
  const value = cleanInfoText(m.equals);
  return isNumberOp(m.op) ? `${m.op}:${Number(value)}` : `${m.op}:${infoMatchKey(value)}`;
}

const DUPLICATE_KEYS: Record<string, string> = {
  equals: 'settings-actions-info-error-equals-duplicate',
  any: 'settings-actions-info-error-any-duplicate',
};

/**
 * Conditions after the first "has any value" can never match (first match
 * wins). The server accepts them; the editor warns.
 */
export function unreachableConditions(field: Pick<EditableInfoField, 'display' | 'matches'>): Set<string> {
  const out = new Set<string>();
  if (field.display !== 'match') return out;
  const firstAny = field.matches.findIndex((m) => m.op === 'any');
  if (firstAny < 0) return out;
  field.matches.slice(firstAny + 1).forEach((m) => out.add(m.rowKey));
  return out;
}

export function validateInfoFields(fields: ReadonlyArray<EditableInfoField>): InfoFieldsValidation {
  let hasErrors = false;
  const matchRows: Array<Array<Partial<Record<InfoMatchInput, string>>>> = [];
  const rows = fields.map((f) => {
    const row: Partial<Record<InfoFieldInput, string>> = {};
    const label = cleanInfoText(f.label);
    if (!label || label.length > INFO_FIELD_LABEL_MAX_LENGTH) {
      row.label = 'settings-actions-info-error-label';
    }
    if (f.display !== 'link' && f.display !== 'externalTag') {
      const attributeKey = f.attributeKey.trim();
      if (!attributeKey || attributeKey.length > INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH) {
        row.attributeKey = 'settings-actions-info-error-attribute-key';
      }
    } else if (f.display === 'link') {
      const checked = checkLinkTemplate(f.linkUrl);
      if ('code' in checked) row.linkUrl = LINK_URL_ERROR_KEYS[checked.code];
    }

    const matchErrors: Array<Partial<Record<InfoMatchInput, string>>> = [];
    if (f.display === 'match') {
      if (f.matches.length === 0) {
        row.matches = 'settings-actions-info-error-matches-required';
      } else if (f.matches.length > INFO_FIELD_MATCHES_MAX) {
        row.matches = 'settings-actions-info-error-matches-max';
      }
      const keyCounts = new Map<string, number>();
      for (const m of f.matches) {
        const key = conditionKey(m);
        if (key) keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1);
      }
      for (const m of f.matches) {
        const match: Partial<Record<InfoMatchInput, string>> = {};
        const valueError = conditionValueError(m);
        const key = conditionKey(m);
        if (valueError) {
          match.equals = valueError;
        } else if (key && (keyCounts.get(key) ?? 0) > 1) {
          match.equals = DUPLICATE_KEYS[m.op] ?? 'settings-actions-info-error-condition-duplicate';
        }
        const text = cleanInfoText(m.text);
        if (!text || text.length > INFO_MATCH_TEXT_MAX_LENGTH) {
          match.text = 'settings-actions-info-error-text';
        }
        if (Object.keys(match).length > 0) hasErrors = true;
        matchErrors.push(match);
      }
    }
    matchRows.push(matchErrors);
    if (Object.keys(row).length > 0) hasErrors = true;
    return row;
  });

  let block: string | null = null;
  if (fields.length > INFO_FIELDS_MAX) {
    block = 'settings-actions-info-error-total-cap';
  } else if (countImportantFields(fields) > INFO_FIELDS_MAX_IMPORTANT) {
    block = 'settings-actions-info-error-important-cap';
  }

  return { rows, matchRows, block, hasErrors: hasErrors || block !== null };
}

/** What the conflict rule reads from an action. */
export interface InfoDivisionCandidate {
  actionId?: string;
  type: string;
  label: string;
  enabled: boolean;
  divisionIds: string[];
}

export interface InfoDivisionClash {
  conflictActionId?: string;
  conflictLabel: string;
  /** The first division both use (`shared`), or the other action's first division (`candidate_all`); null for `other_all`. */
  divisionId: string | null;
  /** Which side covers all divisions; picks the message. */
  via: 'shared' | 'candidate_all' | 'other_all';
}

/**
 * One enabled Conversation info action per division (the toolbar's
 * `findInfoDivisionConflict`): an enabled action with no divisions covers
 * every division and conflicts with any other enabled info action; two
 * division-scoped actions conflict when their lists intersect. Disabled
 * actions never conflict. `others` are the saved actions, which are what the
 * toolbar checks a save against.
 */
export function findInfoDivisionConflict(
  candidate: InfoDivisionCandidate,
  others: Iterable<InfoDivisionCandidate>,
): InfoDivisionClash | null {
  if (candidate.type !== CONVERSATION_INFO_TYPE || !candidate.enabled) return null;
  const mine = candidate.divisionIds;
  for (const other of others) {
    if (other.type !== CONVERSATION_INFO_TYPE || !other.enabled) continue;
    if (candidate.actionId && other.actionId === candidate.actionId) continue;
    const theirs = other.divisionIds;
    const conflict = { ...(other.actionId ? { conflictActionId: other.actionId } : {}), conflictLabel: other.label.trim() };
    if (theirs.length === 0) return { ...conflict, divisionId: null, via: 'other_all' };
    if (mine.length === 0) return { ...conflict, divisionId: theirs[0], via: 'candidate_all' };
    const shared = mine.find((d) => theirs.includes(d));
    if (shared) return { ...conflict, divisionId: shared, via: 'shared' };
  }
  return null;
}

/**
 * The admin-facing sentence for a clash. `divisionName` resolves an id from
 * the loaded divisions list (null when unknown or the list is unavailable),
 * so the division is named whenever it can be.
 */
export function infoConflictMessage(
  clash: Pick<InfoDivisionClash, 'conflictLabel' | 'divisionId' | 'via'>,
  divisionName: (id: string) => string | null,
): string {
  const name = clash.divisionId ? divisionName(clash.divisionId) : null;
  // One pass, so an admin-typed label or division name containing a placeholder is left as typed.
  const fill = (key: string) => t(key, {
    name: clash.conflictLabel,
    division: name ?? '',
  });
  if (clash.via === 'other_all') return fill('settings-actions-info-conflict-all-used');
  if (clash.via === 'candidate_all') {
    return fill(name ? 'settings-actions-info-conflict-covers-all' : 'settings-actions-info-conflict-covers-all-unnamed');
  }
  return fill(name ? 'settings-actions-info-conflict-division' : 'settings-actions-info-conflict-this-division');
}
